// the channel's own music player. ws4kp's player shuffles one folder; this one plays the tracks tagged for the
// current daypart's mode (config/clock.json, music/tracks.json) and can duck under bumpers later.
// The build gives ws4kp an empty playlist.json so the two never play at once.
import { parseQueryString } from '../utils/setting.mjs';
import setHeadend from '../headend.mjs';

const { clock } = window.RHL;
const PLAYLIST_URL = 'rhl/playlist.json';
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

let tracks = [];
let queue = [];
let current = null;
let player = null;
let mode = null;
let baseVolume = 0.7;
let duckGain = 1;
let consecutiveErrors = 0;
let muted = false;
// the browser is holding sound back until the first tap, click or key (Oct 26: the controls show a prompt meanwhile)
let blocked = false;
const listeners = new Set();
const blockedListeners = new Set();
const setBlocked = (value) => {
	if (blocked === value) return;
	blocked = value;
	blockedListeners.forEach((fn) => fn(blocked));
};

// the daypart in the channel's own time zone, since the encoder's clock is UTC
const daypart = (date = new Date()) => {
	const parts = new Intl.DateTimeFormat('en-US', {
		timeZone: clock.timeZone, hour: 'numeric', hourCycle: 'h23', weekday: 'short',
	}).formatToParts(date);
	const hour = Number(parts.find((p) => p.type === 'hour').value);
	const weekday = WEEKDAYS.indexOf(parts.find((p) => p.type === 'weekday').value);
	const part = clock.dayparts.find(({ start, end }) => (start < end ? hour >= start && hour < end : hour >= start || hour < end));
	return { name: part?.name ?? 'day', weekend: clock.weekend.includes(weekday) };
};

const musicMode = (date) => {
	const { name } = daypart(date);
	return clock.music[name] ?? name;
};

const shuffle = (list) => {
	const result = [...list];
	for (let i = result.length - 1; i > 0; i -= 1) {
		const j = Math.floor(Math.random() * (i + 1));
		[result[i], result[j]] = [result[j], result[i]];
	}
	return result;
};

// tracks tagged for the mode; untagged tracks play in any mode; if nothing fits, everything plays
const tracksFor = (forMode) => {
	const tagged = tracks.filter((t) => t.modes?.includes(forMode));
	if (tagged.length) return tagged;
	const untagged = tracks.filter((t) => !t.modes?.length);
	return untagged.length ? untagged : tracks;
};

const trackName = (track) => [track.title, track.artist].filter(Boolean).join(' - ');

const applyVolume = () => {
	if (player) {
		player.volume = Math.max(0, Math.min(1, baseVolume * duckGain));
		player.muted = muted;
	}
};

const nextTrack = () => {
	const nowMode = musicMode();
	if (nowMode !== mode || queue.length === 0) {
		mode = nowMode;
		// don't repeat the track that just finished at the top of a fresh shuffle
		queue = shuffle(tracksFor(mode));
		if (queue.length > 1 && queue[0] === current) queue.push(queue.shift());
	}
	current = queue.shift();
	player.src = current.file;
	setHeadend('music', `${trackName(current)} (${mode})`);
	listeners.forEach((fn) => fn(current));
	return play();
};

const waitForInteraction = () => {
	setHeadend('music', 'Waiting for a click or key press to start');
	// touchend and pointerup count as well as click: on an iPhone (every browser there runs Safari's engine) a tap
	// whose hover step changes the page is taken as a hover and sends no click at all, so a click-only listener can
	// need a second tap (Oct 26)
	const types = ['touchend', 'pointerup', 'click', 'keydown'];
	const start = () => {
		types.forEach((type) => document.removeEventListener(type, start, { capture: true }));
		play();
	};
	types.forEach((type) => document.addEventListener(type, start, { capture: true }));
};

const play = async () => {
	try {
		await player.play();
		setBlocked(false);
	} catch (error) {
		// the browser won't start sound before the page is interacted with; the encoder launches with autoplay allowed
		if (error.name === 'NotAllowedError') {
			setBlocked(true);
			waitForInteraction();
		}
		else console.error('Russian Hill Local music:', error);
	}
};

const start = () => {
	player = new Audio();
	applyVolume();
	player.addEventListener('ended', () => {
		consecutiveErrors = 0;
		nextTrack();
	});
	// a missing or broken file moves on, but a whole playlist of failures stops instead of spinning
	player.addEventListener('error', () => {
		consecutiveErrors += 1;
		if (consecutiveErrors > tracks.length) {
			setHeadend('music', 'Every track failed to load');
			return;
		}
		setTimeout(nextTrack, 1000);
	});
	nextTrack();
};

const init = async () => {
	const params = parseQueryString();
	if (params.mediaVolume !== undefined && !Number.isNaN(Number(params.mediaVolume))) baseVolume = Number(params.mediaVolume);
	try {
		const response = await fetch(PLAYLIST_URL);
		if (!response.ok) throw new Error(`${PLAYLIST_URL} ${response.status}`);
		({ tracks } = await response.json());
	} catch (error) {
		console.error('Russian Hill Local music: no playlist', error);
		return;
	}
	if (!tracks.length) return;
	if (params.mediaPlaying === 'true') start();
};

// a bumper calls duck(6) and then duck(0); dB below the set volume
const duck = (db) => {
	duckGain = 10 ** (-Math.max(0, db) / 20);
	applyVolume();
};

// the control bar's sound button: mutes the music and, through isMuted(), the bumpers' bell and voice
const isMuted = () => muted;
const toggleMute = () => {
	muted = !muted;
	applyVolume();
	return muted;
};

const soundBlocked = () => blocked;
const onSoundBlocked = (fn) => blockedListeners.add(fn);
const nowPlaying = () => current;
const onTrackChange = (fn) => listeners.add(fn);

init();

export {
	daypart,
	musicMode,
	duck,
	nowPlaying,
	onTrackChange,
	isMuted,
	toggleMute,
	soundBlocked,
	onSoundBlocked,
};
